import React, { useState } from 'react';

interface PointsAdjustmentModalProps {
  onConfirm: (amount: number, reason: string) => void;
  onCancel: () => void;
  loading: boolean;
}

const PointsAdjustmentModal: React.FC<PointsAdjustmentModalProps> = ({
  onConfirm,
  onCancel,
  loading
}) => {
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>('add');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const numericAmount = parseInt(amount);
    if (isNaN(numericAmount) || numericAmount === 0 || !reason.trim()) {
      return;
    }

    const finalAmount = adjustmentType === 'subtract' ? -numericAmount : numericAmount;
    onConfirm(finalAmount, reason.trim());
  };

  const predefinedReasons = [
    'System error correction',
    'Customer service adjustment',
    'Billing dispute resolution',
    'Feature malfunction compensation',
    'Promotional bonus',
    'Migration adjustment',
    'Manual correction'
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgba(15, 23, 42, 0.95)',
        border: '1px solid rgba(71, 85, 105, 0.5)',
        borderRadius: '16px',
        padding: '2rem',
        width: '100%',
        maxWidth: '500px',
        margin: '1rem'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{
            color: '#e5e7eb',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            ⚖️ Adjust User Points
          </h2>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
            Add or subtract points with a reason for audit trail
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Adjustment Type */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#9ca3af',
              fontSize: '0.875rem',
              marginBottom: '0.5rem'
            }}>
              Action Type
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <AdjustmentTypeButton
                active={adjustmentType === 'add'}
                onClick={() => setAdjustmentType('add')}
                color="#10b981"
                label="➕ Add Points"
                disabled={loading}
              />
              <AdjustmentTypeButton
                active={adjustmentType === 'subtract'}
                onClick={() => setAdjustmentType('subtract')}
                color="#ef4444"
                label="➖ Subtract Points"
                disabled={loading}
              />
            </div>
          </div>

          {/* Amount Input */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#9ca3af',
              fontSize: '0.875rem',
              marginBottom: '0.5rem'
            }}>
              Points Amount
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter points amount..."
              min="1"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '0.875rem',
                outline: 'none',
                opacity: loading ? 0.6 : 1
              }}
            />
          </div>

          {/* Reason Selection */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#9ca3af',
              fontSize: '0.875rem',
              marginBottom: '0.5rem'
            }}>
              Reason for Adjustment
            </label>
            
            {/* Predefined Reasons */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              {predefinedReasons.map((predefinedReason) => (
                <button
                  key={predefinedReason}
                  type="button"
                  onClick={() => setReason(predefinedReason)}
                  disabled={loading}
                  style={{
                    padding: '0.5rem',
                    background: reason === predefinedReason 
                      ? 'rgba(6, 182, 212, 0.3)' 
                      : 'rgba(71, 85, 105, 0.3)',
                    border: '1px solid rgba(71, 85, 105, 0.5)',
                    borderRadius: '6px',
                    color: reason === predefinedReason ? '#06b6d4' : '#9ca3af',
                    fontSize: '0.75rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  {predefinedReason}
                </button>
              ))}
            </div>

            {/* Custom Reason Input */}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Or enter a custom reason..."
              rows={3}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '0.875rem',
                outline: 'none',
                resize: 'vertical',
                opacity: loading ? 0.6 : 1
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(71, 85, 105, 0.5)',
                color: '#9ca3af',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading || !amount || !reason.trim() || parseInt(amount) <= 0}
              style={{
                padding: '0.75rem 1.5rem',
                background: loading || !amount || !reason.trim() || parseInt(amount) <= 0
                  ? 'rgba(71, 85, 105, 0.5)'
                  : adjustmentType === 'add'
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading || !amount || !reason.trim() || parseInt(amount) <= 0 
                  ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                opacity: loading || !amount || !reason.trim() || parseInt(amount) <= 0 ? 0.6 : 1
              }}
            >
              {loading ? 'Processing...' : `${adjustmentType === 'add' ? 'Add' : 'Subtract'} Points`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AdjustmentTypeButtonProps {
  active: boolean;
  onClick: () => void;
  color: string;
  label: string;
  disabled: boolean;
}

const AdjustmentTypeButton: React.FC<AdjustmentTypeButtonProps> = ({
  active,
  onClick,
  color,
  label,
  disabled
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      flex: 1,
      padding: '0.75rem',
      background: active ? `${color}20` : 'rgba(71, 85, 105, 0.3)',
      border: active ? `1px solid ${color}50` : '1px solid rgba(71, 85, 105, 0.5)',
      borderRadius: '8px',
      color: active ? color : '#9ca3af',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.2s'
    }}
  >
    {label}
  </button>
);

export default PointsAdjustmentModal;
